// 장비 저장(dungeonhero.gear.v1, D2a·D2b): 가방·장착·강화석·보호 주문서·강화 장부. 규칙은 shared/gear.ts·gearLedger.ts(순수).
// 기존 저장(save.v1 보상·profile.v2 상점 장부·growth.v2 성장)과 키를 나눠 그 형식은 바꾸지 않는다(이전 빌드로 되돌려도 그대로 읽힌다).
// 쓰기: 저장소 최신을 다시 읽어 기준으로 삼고(rev가 큰 쪽), 이 탭의 미기록 연산을 그 위에 다시 적용한 뒤 쓰고 되읽어 확인한다.
// 연산은 id로 멱등이라 재시도·탭 합치기·재로드에서 두 번 적용되지 않는다. 손상 원문은 .bak/.latest에 남기고 마지막 정상 사본(.good)으로 잇는다.
// 줍기는 고정 스텝에서 메모리에만 반영하고(flushSaves → flushIdle이 렌더 프레임에서 1초에 한 번 이하로 합쳐 쓴다), 마을 명령(장착·강화·정리)은 바로 쓰고 결과를 돌려준다.
import { OWN_OPS_CAP } from '../shared/content/loot';
import type { EquipSlot, HeroId } from '../shared/content/types';
import { itemHeroLock, makeGearTotals, socketSig, sumGear, type GearItem, type GearTotals } from '../shared/gear';
import { ELIXIRS } from '../shared/content/consumables';
import type { MasterySave } from '../shared/mastery';
import {
  HW_HEROES, applyGearOp, cloneGear, equippedItems, hasApplied, weaponUidOf, isApplied, freshGear, parseGear, serializeGear, type ApplyResult, type EnhanceLog, type GearCode, type GearOp, type GearState,
} from '../shared/gearLedger';
import type { GearCmdKind, GearMode, GearNoticeView, GearResultView, GearView } from '../shared/gearView';
import { rewardId, type SaveStorage } from './save';
import { storageEventMatches } from '../characters/keys';

export type { GearCmdKind, GearMode, GearNoticeView, GearResultView, GearView };

export const GEAR_KEY = 'dungeonhero.gear.v1';
export const GEAR_BACKUP_KEY = GEAR_KEY + '.bak';
export const GEAR_LATEST_BACKUP_KEY = GEAR_BACKUP_KEY + '.latest';
export const GEAR_GOOD_KEY = GEAR_KEY + '.good';
/** 쓰기 후 되읽기 확인 재시도 수 */
const WRITE_ATTEMPTS = 5;
/** 쓰기 실패 뒤 다시 시도하는 간격(ms, 렌더 프레임 flush가 매 프레임 저장소를 두드리지 않게) */
const RETRY_MS = 2000;
/**
 * 줍기 저장 합치기 간격(ms, c015): 렌더 프레임 flushIdle은 첫 미기록 연산 뒤 이만큼 지나야 쓴다(1초에 한 번 이하).
 * 판 시작·클리어·패배·마을 명령·페이지 숨김(pagehide·visibilitychange hidden)은 flush(true)로 바로 쓴다 — 쓰기를 잃지 않는다
 */
export const GEAR_FLUSH_DEBOUNCE_MS = 1000;
const SEEN_LIMIT = 64;

type ReadResult = { kind: 'none' } | { kind: 'ok'; state: GearState } | { kind: 'corrupt'; raw: string } | { kind: 'future' } | { kind: 'unavailable' };
type Mutable<T> = { -readonly [K in keyof T]: T[K] };

export class GearStore implements GearView {
  mode: GearMode = 'ok';
  revision = 0;
  lastSaveOk = true;
  recovered = 0;
  /** 마지막 명령의 장부 적용 결과(초월 판정 등, 실패·거절이면 null) */
  lastApply: ApplyResult | null = null;
  readonly last: Mutable<GearResultView> = { seq: 0, kind: 'none', code: 'ok', uid: '', requestId: '', outcome: '', from: 0, to: 0, chance: 0 };
  readonly notice: Mutable<GearNoticeView> = { seq: 0, kind: 'none', code: 'ok', uid: '' };
  readonly totals: GearTotals = makeGearTotals();
  /**
   * 무기 칸을 뺀 장비 합계(c022). 장비 장부는 계정 공유이고 무기 칸은 하나라서(gear.v1 형식 그대로) 무기가 영웅별이 되기 전까지
   * 카엘 전용 무기를 쓰지 못하는 영웅(라이라)은 이 합계로 싸운다. 무기 칸 = SLOT_ORDER[0]
   */
  readonly totalsNoWeapon: GearTotals = makeGearTotals();
  /** 장착 무기의 영웅 전용(없으면 null). gearTotalsOf가 이 값으로 totals·totalsNoWeapon을 고른다 */
  weaponLock: string | null = null;
  /** 강화 상한·보호 주문서 해금(GameSim이 마을 진행에서 정한다) */
  enhanceCap = 5;
  protectUnlocked = false;
  town = 1;
  /** 저장소에서 확인한 마지막 상태 */
  private confirmed: GearState = freshGear();
  /** 화면·전투가 보는 상태 = confirmed + 미기록 연산 */
  private working: GearState = freshGear();
  /** 아직 저장소에 확인되지 않은 연산(이 탭) */
  private pending: GearOp[] = [];
  /** 이 탭이 쓴 연산(다른 탭이 오래된 기준으로 덮어써 빠졌는지 다음 읽기에서 확인한다) */
  private own: GearOp[] = [];
  private readonly seen = new Set<string>();
  private readonly nonce: string;
  private retryAt = 0;
  /** 마지막 flush·rebuild에서 새 기준 위에 다시 적용했을 때 거절된 연산 id → 거절 코드(명령이 성공이라 알리지 않게) */
  private readonly rejected = new Map<string, GearCode>();
  /** 지금 command()가 결과를 직접 돌려줄 연산 id(그 연산의 거절은 명령 결과로 알리므로 알림을 내지 않는다) */
  private commandOp = '';
  /** 이 탭 연산 순번(단조). 연산에 src = nonce와 함께 붙여 저장 수위표로 중복을 판정한다 */
  private seq = 0;
  private readDirty = false;
  /** 미기록 연산이 없어도 다음 flush에서 써야 한다(손상 복구·오래된 덮어쓰기 복원) */
  private forceWrite = false;
  private readonly eqScratch: (GearItem | null)[] = [];
  /** 합계를 마지막으로 계산한 장착 상태(부위별 uid·강화 단계). 같으면 sumGear를 건너뛴다(c015) */
  private readonly eqUid: (string | null)[] = [null, null, null, null, null, null];
  private readonly eqEnh = new Int16Array(6).fill(-1);
  /** 부위별 소켓 서명(D2c: 보석을 박거나 빼면 합계를 다시 계산한다) */
  private readonly eqSock = new Float64Array(6).fill(-1);
  /**
   * 플레이 시계(접속 시간 초, D2c 비약). 고정 스텝(tickPlay)이 마을·던전에서 60틱마다 1 올린다. 저장 기준과 합칠 때는 큰 쪽(오프라인 동안 멈춘다).
   * 연산이 아니라 상태 칸이라 탭 둘이 동시에 켜져 있어도 두 배로 흐르지 않는다(큰 쪽 = 벽시계와 같은 속도)
   */
  private playSec = 0;
  private playTicks = 0;
  /** 마지막으로 저장소에 쓴 플레이 시계 */
  private playWritten = 0;
  /** 비약이 켜져 있는 동안 플레이 시계를 이 간격(초)마다 저장한다(꺼져 있으면 다른 쓰기에 실려 간다) */
  static readonly PLAY_SAVE_SEC = 10;
  private totalsInit = false;
  /** 장비 합계를 실제로 다시 계산한 횟수(QA·시험: 가방·강화석 줍기에서는 오르지 않는다) */
  totalsComputed = 0;
  /** 아직 쓰지 않은 연산이 처음 생긴 시각(clock, 없으면 −1). flushIdle 합치기 기준 */
  private dirtySince = -1;
  private readonly clock: () => number;
  private readonly onStorage = (e: StorageEvent): void => {
    // 캐릭터 저장소면 실제 영역·캐릭터 키로 비교한다(src/characters/keys.ts)
    if (storageEventMatches(this.storage, e, GEAR_KEY)) this.readDirty = true;
  };

  constructor(private readonly storage: SaveStorage, options: { nonce?: string; clock?: () => number } = {}) {
    this.nonce = options.nonce ?? rewardId();
    this.clock = options.clock ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
    this.refresh();
    if (typeof window !== 'undefined') window.addEventListener('storage', this.onStorage);
  }
  dispose(): void { if (typeof window !== 'undefined') window.removeEventListener('storage', this.onStorage); }

  // ── 보기 ──
  get items(): readonly GearItem[] { return this.working.items; }
  /**
   * 지금 영웅이 보는 장착(무기 = 그 영웅 무기 칸, lyra-weapon-slot). 화면·전투·추천이 모두 이 보기를 읽으므로 영웅을 바꾸면 무기 칸이 바뀐다.
   * 장부 원본(equipped + hw)은 heroWeapons·snapshot으로 본다
   */
  get equipped(): Readonly<Record<EquipSlot, string | null>> {
    // 할당 없이 고정 객체에 6칸을 옮겨 담는다(매번 새로 — 캐시가 낡을 일이 없다)
    const w = this.working, v = this.eqView;
    v.weapon = weaponUidOf(w, this.hero); v.helm = w.equipped.helm; v.armor = w.equipped.armor; v.gloves = w.equipped.gloves; v.boots = w.equipped.boots; v.amulet = w.equipped.amulet;
    return v;
  }
  private readonly eqView: Record<EquipSlot, string | null> = { weapon: null, helm: null, armor: null, gloves: null, boots: null, amulet: null };
  /** 영웅별 무기 칸 전체(카엘 = equipped.weapon 원본, 다른 영웅 = hw). 가방 목록에서 "다른 영웅이 낀 무기"를 가리는 데 쓴다 */
  get heroWeapons(): Readonly<Partial<Record<HeroId, string>>> {
    const w = this.working, o = this.hwView;
    o.kael = w.equipped.weapon ?? undefined;
    for (const h of HW_HEROES) o[h] = w.hw[h];
    return o;
  }
  private readonly hwView: Partial<Record<HeroId, string>> = {};
  /** 장착 보기·합계를 계산하는 영웅(GameSim이 영웅을 고를 때 setHero). 기본 카엘(옛 동작) */
  hero: HeroId = 'kael';
  /** 영웅이 바뀌면 무기 칸 보기와 합계를 다시 낸다(같으면 아무것도 하지 않는다 — 매 틱 불러도 된다) */
  setHero(h: HeroId): void {
    if (this.hero === h) return;
    this.hero = h;
    this.changed();
  }
  get stones(): readonly number[] { return this.working.stones; }
  get protects(): number { return this.working.protects; }
  get goldNet(): number { return this.working.goldNet; }
  /** 숙련·초월(C4, 화면 값 = 미기록 포함) */
  get ms(): MasterySave { return this.working.ms; }
  /**
   * 골드 잔액에 쓰는 장비 순지출: 저장 확인된 값 + 아직 확인 안 된 지출(강화·주문서). 확인 안 된 수입(자동 정리·판매)은 넣지 않는다 —
   * 장비 저장이 실패한 채 그 수입으로 상점 구매(profile.v2에 확정)를 하면 다시 읽었을 때 잔액이 음수가 된다(교차 리뷰 P2).
   */
  get committedGoldNet(): number { return this.confirmed.goldNet + this.pendingSpend; }
  /** 미기록 연산의 골드 지출 합(양수만) */
  private pendingSpend = 0;
  get log(): readonly EnhanceLog[] { return this.working.log; }
  get cons(): Readonly<Record<string, number>> { return this.working.cons; }
  get gems(): readonly number[] { return this.working.gems; }
  get elix(): readonly number[] { return this.working.elix; }
  get play(): number { return this.playSec; }
  /** 켜진 비약 비트(ELIXIRS 순서, 지금 플레이 시계 기준). 할당 없음 */
  elixirMask(): number {
    let m = 0;
    const e = this.working.elix;
    for (let k = 0; k < e.length; k++) if (e[k] > this.playSec) m |= 1 << k;
    return m;
  }
  /**
   * 고정 스텝(마을·던전, 일시정지 아님): 60틱마다 플레이 시계 +1초. 비약이 켜져 있으면 PLAY_SAVE_SEC마다 다음 합친 저장에 싣는다.
   * 할당 없음(비교·정수 증가만)
   */
  tickPlay(): void {
    if (++this.playTicks < 60) return;
    this.playTicks = 0;
    this.playSec++;
    if (this.playSec - this.playWritten >= GearStore.PLAY_SAVE_SEC && this.elixirMask() !== 0 && !this.forceWrite) {
      this.forceWrite = true;
      if (this.dirtySince < 0) this.dirtySince = this.clock();
    }
  }
  get pendingOps(): number { return this.pending.length; }
  /**
   * 이 개체를 아직 가지고 있을 수 있나(거래소 맡기기 ack 판단). 확인된 저장에 없고 대기 연산·실패 저장이 없을 때만 false —
   * 작업 메모리에서만 빠진 상태(저장 미확인)로 ack해 저장소 사본이 남던 문제(PR #246 Codex P1)
   */
  mayHold(uid: string): boolean {
    this.refresh();
    if (this.mode !== 'ok') return true;
    // 확인 안 된 상태면 지금 한 번 써서 되읽어 확인한다(대기 연산이 없어도 마지막 저장이 미확인일 수 있다)
    if (this.pending.length > 0 || this.forceWrite || !this.lastSaveOk) { this.forceWrite = true; this.flushResult(true); }
    if (!this.lastSaveOk || this.pending.length > 0 || this.forceWrite) return true;
    return this.confirmed.items.some((it) => it.uid === uid);
  }
  /** 장부 상태 사본(테스트·QA) */
  snapshot(): GearState { return cloneGear(this.working); }
  equippedItems(): (GearItem | null)[] { return equippedItems(this.working, this.eqScratch, this.hero); }

  /** 상태가 바뀌었다: 장착 부위(uid·강화 단계)가 달라졌을 때만 합계를 다시 계산하고, 화면 키(revision)는 늘 올린다 */
  private changed(): void {
    const eq = equippedItems(this.working, this.eqScratch, this.hero);
    let same = this.totalsInit;
    for (let k = 0; k < eq.length && same; k++) {
      const it = eq[k];
      if ((it ? it.uid : null) !== this.eqUid[k] || (it ? it.enh : -1) !== this.eqEnh[k] || (it ? socketSig(it) : -1) !== this.eqSock[k]) same = false;
    }
    if (!same) {
      sumGear(this.totals, eq);
      const w = eq[0]; this.weaponLock = itemHeroLock(w); eq[0] = null;
      sumGear(this.totalsNoWeapon, eq);
      eq[0] = w;
      for (let k = 0; k < eq.length; k++) { const it = eq[k]; this.eqUid[k] = it ? it.uid : null; this.eqEnh[k] = it ? it.enh : -1; this.eqSock[k] = it ? socketSig(it) : -1; }
      this.totalsInit = true;
      this.totalsComputed++;
    }
    this.revision++;
  }

  // ── 읽기 ──
  private read(): ReadResult {
    let raw: string | null;
    try { raw = this.storage.getItem(GEAR_KEY); } catch { return { kind: 'unavailable' }; }
    if (raw === null) return { kind: 'none' };
    const p = parseGear(raw);
    if (p.ok) return { kind: 'ok', state: p.state };
    return p.reason === 'future' ? { kind: 'future' } : { kind: 'corrupt', raw };
  }
  private readGood(): GearState | null {
    try {
      const raw = this.storage.getItem(GEAR_GOOD_KEY);
      if (raw === null) return null;
      const p = parseGear(raw);
      return p.ok ? p.state : null;
    } catch { return null; }
  }
  /** 손상 원문을 .bak(최초)·.latest(이후)에 남기고 되읽어 확인한다(save.v1·growth.v2와 같은 방식) */
  private backupCorrupt(raw: string): boolean {
    try {
      const first = this.storage.getItem(GEAR_BACKUP_KEY);
      if (first === null) this.storage.setItem(GEAR_BACKUP_KEY, raw);
      else if (first !== raw && this.storage.getItem(GEAR_LATEST_BACKUP_KEY) !== raw) this.storage.setItem(GEAR_LATEST_BACKUP_KEY, raw);
      return this.storage.getItem(GEAR_BACKUP_KEY) === raw || this.storage.getItem(GEAR_LATEST_BACKUP_KEY) === raw;
    } catch { return false; }
  }
  private setMode(m: GearMode): void { if (this.mode !== m) { this.mode = m; this.revision++; } }

  /**
   * 저장소 최신으로 기준을 맞춘다(생성·마을 진입·명령 전·다른 탭 변경 알림). 기준 = 저장소와 이 탭 확인본 중 rev가 큰 쪽(같으면 저장소).
   * 이 탭이 썼지만 기준에 없는 연산(다른 탭이 오래된 기준으로 덮어씀)은 다시 미기록으로 돌린다. working = 기준 + 미기록 연산.
   */
  refresh(): void {
    this.readDirty = false;
    const r = this.read();
    let base: GearState | null = null;
    switch (r.kind) {
      case 'unavailable': this.setMode('unavailable'); break;
      case 'future': this.setMode('future'); break;
      case 'none': base = this.confirmed; this.setMode('ok'); break;
      case 'ok':
        base = r.state.rev >= this.confirmed.rev ? r.state : this.confirmed;
        // 저장소가 이 탭 확인본보다 오래됐다(오래된 기준의 쓰기가 덮어씀): 이 탭 확인본을 다시 쓴다. 그 탭은 자기 연산을 다음 읽기에서 다시 적용한다
        if (base !== r.state) this.forceWrite = true;
        this.setMode('ok');
        break;
      case 'corrupt': {
        if (!this.backupCorrupt(r.raw)) { this.setMode('unavailable'); break; }
        const good = this.readGood();
        if (!good) { this.setMode('corrupt'); break; }
        this.recovered++;
        base = good.rev >= this.confirmed.rev ? good : this.confirmed;
        this.setMode('ok');
        // 주 저장을 정상 사본(+ 이 탭 미기록 연산)으로 다시 쓰도록 다음 flush를 예약한다
        this.forceWrite = true;
        break;
      }
    }
    if (!base) { this.changed(); return; }
    this.confirmed = base;
    // 플레이 시계: 저장된 값과 이 탭 값 중 큰 쪽(다른 탭이 더 오래 켜져 있었으면 그 시각부터)
    if (base.play > this.playSec) { this.playSec = base.play; this.playTicks = 0; }
    if (base.play > this.playWritten) this.playWritten = base.play;
    // 덮어써져 빠진 이 탭 연산 → 다시 미기록으로(수위표·applied로 판정, 기준에 이미 있으면 적용되지 않는다).
    // 확인된 연산도 한 번 보였다고 버리지 않는다: 같은 rev에서 늦게 쓴 다른 탭(오래된 기준)이 나중에 덮어쓸 수 있다(교차 리뷰 P1).
    // own은 최근 OWN_OPS_CAP개를 계속 들고 다니며 새 기준마다 다시 확인한다.
    for (const op of this.lostOwn(base)) this.pending.push(op);
    this.rebuild();
  }
  /** 이 탭이 쓴 연산 중 기준에 없는 것(다른 탭이 오래된 기준으로 덮어씀). 미기록 목록에 이미 있는 것은 뺀다 */
  private lostOwn(base: GearState): GearOp[] {
    return this.own.filter((op) => !isApplied(base, op) && !this.pending.some((p) => p.id === op.id));
  }
  /** 이 탭 연산은 만든 순서(seq)대로 적용해야 수위표가 맞다 */
  private sortPending(): void { this.pending.sort((a, b) => (a.seq ?? 0) - (b.seq ?? 0)); }

  /** working = confirmed + 미기록 연산. 기준이 바뀌어 더는 적용되지 않는 연산(duplicate 등)은 버린다 */
  private rebuild(): void {
    this.sortPending();
    const w = cloneGear(this.confirmed);
    const keep: GearOp[] = [];
    let spend = 0;
    for (const op of this.pending) {
      const r = applyGearOp(w, op);
      if (r.code === 'ok' || r.code === 'auto_salvaged') { keep.push(op); if ((r.gold ?? 0) > 0) spend += r.gold!; }
      else if (r.code !== 'duplicate') this.reject(op, r.code);
    }
    this.pendingSpend = spend;
    this.pending = keep;
    // 미기록 연산이 모두 빠졌으면(되돌림·중복·거절) 합치기 시계를 멈춘다: 다음 줍기가 1초를 새로 센다(PR #157 리뷰 P2)
    if (keep.length === 0 && !this.forceWrite) this.dirtySince = -1;
    this.working = w;
    this.changed();
  }

  /** 고정 스텝 시작: 다른 탭이 이 키를 바꿨다는 알림이 있을 때만 다시 읽는다(평소에는 비교만) */
  tick(): void { if (this.readDirty) this.refresh(); }

  // ── 쓰기 ──
  /**
   * 미기록 연산을 저장소 최신 위에 적용해 쓰고 되읽어 확인한다. 성공하면 confirmed = 되읽은 상태.
   * 실패하면 미기록 연산을 그대로 두고(줍기는 다음 flush에서 다시) false.
   */
  flush(force = false): boolean { return this.flushResult(force) === 'ok'; }

  /**
   * 렌더 프레임용 합친 저장(GameSim.flushSaves, c015): 미기록 연산이 생긴 뒤 GEAR_FLUSH_DEBOUNCE_MS가 지났을 때만 쓴다.
   * 판 도중 줍기마다 동기 getItem·setItem·JSON을 하지 않고 1초에 한 번 이하로 묶는다. 장면 전환·페이지 숨김은 flush(true)가 바로 쓴다
   */
  flushIdle(): boolean {
    if (this.pending.length === 0 && !this.forceWrite) return this.lastSaveOk;
    const now = this.clock();
    if (this.dirtySince < 0) this.dirtySince = now;
    if (now - this.dirtySince < GEAR_FLUSH_DEBOUNCE_MS) return this.lastSaveOk;
    return this.flush();
  }

  /**
   * ok: 쓰고 되읽어 확인함. failed: 아무것도 쓰지 못함(주 저장 setItem 실패·잠금) — 호출자가 연산을 되돌려도 안전하다.
   * unverified: 주 저장 쓰기는 성공했지만 되읽기 확인을 못 함(읽기 오류·다른 탭 경합) — 저장소에 이미 들어 있을 수 있으므로 되돌리지 않고
   * 미기록으로 남겨 다음 flush에서 확인한다(연산 id·수위표로 멱등이라 두 번 적용·두 번 청구되지 않는다, 교차 리뷰 P2).
   */
  private flushResult(force = false): 'ok' | 'failed' | 'unverified' {
    if (this.pending.length === 0 && !this.forceWrite) return this.lastSaveOk ? 'ok' : 'failed';
    if (!force && !this.lastSaveOk && this.clock() < this.retryAt) return 'failed';
    if (this.mode !== 'ok') { this.fail(); return 'failed'; }
    let wrote = false;
    const unverified = (base: GearState): 'unverified' => {
      // 우리가 쓴 상태 = base + 미기록 연산. 화면은 그 상태로 두고 미기록은 남긴다
      this.confirmed = base;
      this.rebuild();
      this.fail();
      return 'unverified';
    };
    let lastBase: GearState = this.confirmed;
    for (let attempt = 0; attempt < WRITE_ATTEMPTS; attempt++) {
      const r = this.read();
      let base: GearState;
      if (r.kind === 'ok') base = r.state.rev >= this.confirmed.rev ? r.state : this.confirmed;
      else if (r.kind === 'none') base = this.confirmed;
      else if (r.kind === 'corrupt') { this.refresh(); if (this.mode !== 'ok') { this.fail(); return wrote ? unverified(lastBase) : 'failed'; } base = this.confirmed; }
      else if (wrote) return unverified(lastBase);
      else { this.setMode(r.kind); this.fail(); return 'failed'; }
      lastBase = base;
      // 기준에서 빠진 이 탭 확인 연산도 다시 넣는다(같은 rev 늦은 덮어쓰기 복구)
      for (const op of this.lostOwn(base)) this.pending.push(op);
      this.sortPending();
      const next = cloneGear(base);
      const want: string[] = [];
      for (const op of this.pending) {
        const res = applyGearOp(next, op);
        if (res.code === 'ok' || res.code === 'auto_salvaged' || res.code === 'duplicate') { want.push(op.id); this.rejected.delete(op.id); }
        // 새 기준에서 더는 성립하지 않는 연산(다른 탭이 그 장비를 장착·정리함 등): 쓰지 않고 거절 코드를 남긴다
        else this.reject(op, res.code);
      }
      next.rev = base.rev + 1; next.by = this.nonce;
      // 플레이 시계는 큰 쪽(연산이 아니다)
      if (next.play < this.playSec) next.play = this.playSec;
      else if (next.play > this.playSec) { this.playSec = next.play; this.playTicks = 0; }
      const json = serializeGear(next);
      try { this.storage.setItem(GEAR_KEY, json); } catch { if (wrote) return unverified(lastBase); this.fail(); return 'failed'; }
      wrote = true;
      try { this.storage.setItem(GEAR_GOOD_KEY, json); } catch { /* 사본 실패는 주 저장 성공을 바꾸지 않는다 */ }
      const back = this.read();
      if (back.kind !== 'ok') return unverified(base);
      // 되읽은 상태에 우리가 쓴 연산이 모두 있어야 성공(다른 탭이 사이에 덮어썼으면 그 위에 다시)
      if (back.state.rev >= next.rev && want.every((id) => this.confirmedIn(back.state, id))) {
        // 이번 기준에서 실제로 받아들여진(적용·이미 적용) 연산만 own에 넣는다. 거절된 연산을 넣으면 나중에 상황이 바뀌었을 때
        // lostOwn이 되살려 조용히 적용한다(예: 거절된 정리가 다른 탭이 벗긴 뒤 장비를 지움, PR #154 Codex 2차 P2)
        const accepted = new Set(want);
        for (const op of this.pending) if (accepted.has(op.id) && !this.own.some((o) => o.id === op.id)) this.own.push(op);
        if (this.own.length > OWN_OPS_CAP) this.own.splice(0, this.own.length - OWN_OPS_CAP);
        this.confirmed = back.state;
        this.pending = [];
        this.forceWrite = false;
        this.playWritten = Math.max(this.playWritten, back.state.play);
        this.lastSaveOk = true;
        this.dirtySince = -1;
        this.rebuild();
        return 'ok';
      }
      this.confirmed = back.state;
    }
    // 끝까지 확인 못 함: 한 번이라도 썼으면 되돌리지 않는다
    if (wrote) return unverified(this.confirmed);
    this.fail();
    return 'failed';
  }
  /**
   * 연산이 새 기준 위에서 거절됐다. pending·own에 있는 연산은 이미 화면에 성공으로 보였으므로(줍기 띠·명령 결과) 처음 거절될 때 한 번 알림을 낸다
   * (c015, PR #154 리뷰 이월 5번: lostOwn으로 다시 적용한 강화가 no_stones로 조용히 사라졌다). 지금 명령의 연산은 명령 결과가 알린다
   */
  private reject(op: GearOp, code: GearCode): void {
    // 옛 장비 전환(장비 통합)은 플레이어 명령이 아니라 알림을 내지 않는다(다음 장부 판본에서 다시 시도한다)
    if (op.t === 'legacy') { this.rejected.set(op.id, code); return; }
    // 레이드 보상(보호 주문서 지급)은 거절될 일이 없다(지급만). 거절되면 다음 정산 주기에 다시 넣는다
    if (op.t === 'protect') { this.rejected.set(op.id, code); return; }
    // 우편·거래소 연산(mk*)은 서버가 ack 전까지 같은 연산 id로 다시 준다: 알림 없이 거절만 기록한다(mailApply가 결과로 판정)
    if (op.t === 'mkIn' || op.t === 'mkGold' || op.t === 'mkOut') { this.rejected.set(op.id, code); return; }
    if (!this.rejected.has(op.id) && op.id !== this.commandOp) {
      const n = this.notice;
      n.seq++; n.code = code;
      n.kind = op.t === 'stones' || op.t === 'gems' || op.t === 'grant' ? 'pickup' : op.t;
      n.uid = op.t === 'pickup' ? op.item.uid : 'uid' in op ? op.uid : '';
      this.revision++;
    }
    this.rejected.set(op.id, code);
  }
  /** 이 연산 id가 확정 장부에 들었거나 이 탭 대기열에 있는지(둘 다 committedGoldNet에 이미 반영된 지출 — 아바타 제작 owed 예약에서 뺀다) */
  knowsOp(id: string): boolean { return hasApplied(this.confirmed, id) || this.pending.some((p) => p.id === id); }
  /** 연산이 상태에 들어 있는지: 수위표·applied 목록, 또는 같은 개체가 다른 연산 id로 이미 있음(줍기 재전송) */
  private confirmedIn(s: GearState, id: string): boolean {
    const op = this.pending.find((p) => p.id === id);
    if (!op) return hasApplied(s, id);
    return isApplied(s, op) || (op.t === 'pickup' && s.items.some((it) => it.uid === op.item.uid));
  }
  /**
   * 이 탭 연산에 src·seq를 붙인다. 호출자가 이 연산을 위해 새로 만든 객체이므로 제자리에 붙인다(줍기마다 사본을 만들지 않는다, c015).
   * 이미 이 탭 표지가 붙은 연산(같은 객체 재전송)은 그대로 둔다 — 같은 연산 id라 장부가 duplicate로 걸러 낸다
   */
  private stamp(op: GearOp): GearOp {
    const m = op as { src?: string; seq?: number };
    if (m.src === this.nonce && typeof m.seq === 'number') return op;
    m.src = this.nonce; m.seq = ++this.seq;
    return op;
  }
  private fail(): void {
    this.lastSaveOk = false;
    this.retryAt = this.clock() + RETRY_MS;
    this.revision++;
  }

  /**
   * 연산을 메모리에 적용한다(저장은 flush). 줍기·강화석처럼 고정 스텝에서 생기는 연산용. 결과 코드를 돌려준다.
   * 잠금 모드(손상·미래·저장소 없음)에서도 화면에는 보이게 적용하지만 저장되지 않는다(lastSaveOk false).
   */
  submit(raw: GearOp): ApplyResult {
    const op = this.stamp(raw);
    const res = applyGearOp(this.working, op);
    if (res.code === 'ok' || res.code === 'auto_salvaged') {
      this.pending.push(op);
      if (this.dirtySince < 0) this.dirtySince = this.clock();
      if ((res.gold ?? 0) > 0) this.pendingSpend += res.gold!;
      // 가방·강화석·보석 줍기와 물약·주문서 사용은 장착을 바꾸지 않는다: 합계를 다시 계산하지 않고 화면 키만 올린다(고정 스텝 안 할당 없음, c015)
      if (op.t === 'pickup' || op.t === 'stones' || op.t === 'gems' || op.t === 'grant' || op.t === 'use' || op.t === 'elixir') this.revision++;
      else this.changed();
    }
    return res;
  }

  /**
   * 손상 복구(PR #154 P2): 주 저장과 정상 사본(.good)이 모두 못 쓰는 corrupt 잠금에서 새 장부로 다시 시작한다.
   * 손상 원문을 .bak/.latest에 남기고 되읽어 확인한 뒤에만 주 저장을 새로 쓴다(백업 실패면 save_failed, 원문 그대로).
   * 잠금 중 주운 미기록 연산은 새 장부 위에 이어 붙인다. 다른 탭이 이미 고쳤거나(손상 아님) 미래 버전이면 덮어쓰지 않고 readonly.
   */
  recover(requestId: string): GearCode {
    if (this.seen.has(requestId)) return 'duplicate';
    this.seen.add(requestId);
    let code: GearCode;
    const r = this.read();
    if (this.mode !== 'corrupt' || r.kind !== 'corrupt') { this.refresh(); code = 'readonly'; }
    else if (!this.backupCorrupt(r.raw)) code = 'save_failed';
    else {
      const fresh = freshGear();
      fresh.rev = 1; fresh.by = this.nonce;
      const json = serializeGear(fresh);
      let ok = false;
      try { this.storage.setItem(GEAR_KEY, json); ok = true; } catch { /* 아래 save_failed */ }
      if (ok) { try { this.storage.setItem(GEAR_GOOD_KEY, json); } catch { /* 사본 실패는 다음 쓰기에서 */ } }
      const back = ok ? this.read() : null;
      if (!back || back.kind !== 'ok') code = 'save_failed';
      else {
        this.confirmed = back.state;
        this.own = [];
        this.setMode('ok');
        this.lastSaveOk = true;
        this.rebuild();
        if (this.pending.length) this.flush(true);
        code = 'ok';
      }
    }
    const l = this.last;
    l.seq++; l.kind = 'recover'; l.code = code; l.uid = ''; l.requestId = requestId; l.outcome = ''; l.from = 0; l.to = 0; l.chance = 0;
    this.revision++;
    return code;
  }

  /**
   * 마을 명령(장착·벗기·강화·정리·주문서 구매): 저장소를 다시 읽고 적용 → 바로 쓴다. 쓰기에 실패하면 그 연산을 되돌리고 save_failed
   * (실패라 알린 연산이 나중에 조용히 확정되지 않게). requestId가 같은 재전송은 duplicate로 조용히 무시한다.
   */
  /**
   * 옛 상점 장비 전환(ECONOMY 14절): command처럼 저장소 기준으로 적용하고 바로 쓰지만, 플레이어 명령이 아니라 화면 결과(last)는 바꾸지 않는다.
   * 연산 id는 넣을 옛 id 목록으로 정해져 탭 두 개가 같은 전환을 해도 한 번만 들어간다(장부 uid 검사도 한 번 더 막는다).
   */
  /** 아직 저장소에서 확인되지 않은 옛 장비 전환 연산이 있는가(확인되면 GameSim이 profile.v2에 전환 완료를 남긴다) */
  get legacyPending(): boolean { return this.pending.some((op) => op.t === 'legacy'); }

  unifyLegacy(add: readonly string[], equip: readonly string[]): GearCode {
    this.refresh();
    if (this.mode !== 'ok') return 'readonly';
    const op = this.stamp({ id: 'legacy:' + add.join(','), t: 'legacy', add, equip });
    const res = applyGearOp(cloneGear(this.working), op);
    if (res.code !== 'ok') return res.code;
    this.pending.push(op);
    this.rejected.delete(op.id);
    const w = this.flushResult(true);
    if (w === 'failed' || this.rejected.has(op.id)) {
      const code: GearCode = w === 'failed' ? 'save_failed' : this.rejected.get(op.id)!;
      this.pending = this.pending.filter((p) => p.id !== op.id);
      this.rebuild();
      return code;
    }
    this.revision++;
    return 'ok';
  }

  /**
   * 우편·거래소 연산(mkIn·mkGold·mkOut, PARTY-TRADE-PRD 1.1): 저장소를 다시 읽고 적용 → 바로 쓴다(unifyLegacy와 같은 경로, 화면 결과 last는 바꾸지 않는다).
   * 연산 id는 서버 거래에서 결정적(mk:m<우편 id>)이라 두 탭·재전송이 같은 우편을 두 번 넣지 않는다. 이미 들어간 연산은 'duplicate'.
   * 'ok'는 저장소 쓰기까지 끝난 뒤에만 돌려준다(쓰기 실패면 되돌리고 save_failed — 서버 ack를 보내면 안 된다).
   */
  applyMarket(op: Extract<GearOp, { t: 'mkIn' | 'mkGold' | 'mkOut' }>): GearCode {
    this.refresh();
    if (this.mode !== 'ok') return 'readonly';
    const made = this.stamp({ ...op });
    // 아직 저장 확인이 안 된 같은 연산이 대기 중이면 성공이 아니다(ack하면 안 된다 — PR #243 Codex P1). 저장되면 다음 시도가 duplicate로 끝난다
    if (this.pending.some((p) => p.id === made.id)) {
      const w0 = this.flushResult(true);
      // 다시 쓰는 사이 저장소 최신 기준(다른 탭이 가방을 채움 등)에서 거절됐다: 대기열에서 빠졌어도 성공이 아니다(PR #243 Codex P2 — 저장 안 된 우편 ack로 아이템 유실)
      if (this.rejected.has(made.id)) {
        const code = this.rejected.get(made.id)!;
        this.pending = this.pending.filter((p) => p.id !== made.id);
        this.rebuild();
        return code;
      }
      if (this.pending.some((p) => p.id === made.id)) return 'save_failed';
      // 확인된 저장 상태에 실제로 들어갔을 때만 duplicate(ack 가능)
      return w0 === 'ok' && isApplied(this.confirmed, made) ? 'duplicate' : 'save_failed';
    }
    const res = applyGearOp(cloneGear(this.working), made);
    if (res.code !== 'ok') return res.code;
    this.pending.push(made);
    this.rejected.delete(made.id);
    const w = this.flushResult(true);
    // 저장 뒤 다시 읽기가 맞지 않음(다른 탭 덮어쓰기 등): 연산은 대기열에 남기고(다음 저장이 다시 시도) ack 불가로 돌려준다
    if (w === 'unverified' && !this.rejected.has(made.id)) { this.revision++; return 'save_failed'; }
    if (w === 'failed' || this.rejected.has(made.id)) {
      const code: GearCode = w === 'failed' ? 'save_failed' : this.rejected.get(made.id)!;
      this.pending = this.pending.filter((p) => p.id !== made.id);
      this.rebuild();
      return code;
    }
    this.revision++;
    return 'ok';
  }

  /** ELIXIRS 칸 수(시험·UI) */
  static readonly ELIXIR_SLOTS = ELIXIRS.length;

  command(kind: Exclude<GearCmdKind, 'none' | 'pickup' | 'recover'>, requestId: string, makeOp: (opId: string) => GearOp | GearCode, uid = ''): GearCode {
    if (this.seen.has(requestId)) return 'duplicate';
    this.seen.add(requestId);
    if (this.seen.size > SEEN_LIMIT) { const o = this.seen.values().next(); if (!o.done) this.seen.delete(o.value); }
    let code: GearCode;
    let res: ApplyResult | null = null;
    this.lastApply = null;
    const madeRaw = makeOp(kind + ':' + requestId);
    const made = typeof madeRaw === 'string' ? madeRaw : this.stamp(madeRaw);
    if (typeof made === 'string') code = made;
    else {
      this.refresh();
      if (this.mode !== 'ok') code = 'readonly';
      else {
        res = applyGearOp(cloneGear(this.working), made);
        code = res.code;
        if (code === 'ok') {
          this.pending.push(made);
          this.rejected.delete(made.id);
          this.commandOp = made.id;
          const w = this.flushResult(true);
          this.commandOp = '';
          if (w === 'failed') {
            // 아무것도 쓰지 못했다: 되돌리고 실패를 알린다(나중에 조용히 확정되지 않게)
            this.pending = this.pending.filter((p) => p.id !== made.id);
            this.rebuild();
            code = 'save_failed'; res = null;
          } else if (this.rejected.has(made.id)) {
            // 미리 검사는 통과했지만 저장소 최신 기준(다른 탭 변경) 위에서 거절됐다: 성공이라 알리지 않고 그 이유를 알린다(PR #154 P2)
            code = this.rejected.get(made.id)!; res = null;
            this.pending = this.pending.filter((p) => p.id !== made.id);
            this.rebuild();
          } else {
            // ok 또는 unverified(쓰기 성공·확인 대기): 저장소에 들어간 결과를 알린다. 확인은 다음 flush가 같은 연산 id로 한다
            // 쓴 결과(저장소 기준으로 다시 적용된 것)를 보고한다: 강화는 장부 마지막 줄이 이 연산이다
            const logged = made.t === 'enhance' ? this.working.log.find((l) => l.op === made.id) : undefined;
            if (logged && res.roll) { res.roll.outcome = logged.outcome; res.roll.from = logged.from; res.roll.to = logged.to; res.roll.chance = logged.chance; }
          }
        }
      }
    }
    this.lastApply = res;
    const l = this.last;
    l.seq++; l.kind = kind; l.code = code; l.uid = uid; l.requestId = requestId;
    l.outcome = res?.roll ? res.roll.outcome : ''; l.from = res?.roll ? res.roll.from : 0; l.to = res?.roll ? res.roll.to : 0; l.chance = res?.roll ? res.roll.chance : 0;
    this.revision++;
    return code;
  }
}
